/**
 * PruefidViewerPage — Viewer für die Prüfidentifikatoren-Tabellen (Excel-Export
 * aus der maco-knowledge-collection).
 *
 * Eine Formatversion hat einen (oder mehrere) Quell-Ordner mit je ~5 Tabellen
 * (Prüf-ID Prozessschritt, Tupel-Übersicht, Objekteigenschaften, …). Der Index
 * (Sheets + Zeilenzahlen) kommt build-time aus artifacts.json; die einzelne
 * Tabelle wird zur Laufzeit unter /pruefids/<release>/<source>/<file>
 * gefetcht (gespiegelt via build-data.ts, analog zum EBD-Viewer).
 *
 * **Stand-Wahl (AP-B).** Route zuerst (`/pruefid/:release` — der Deep-Link
 * gewinnt und zieht die Kopfzeile nach), ohne Route folgt die Seite der
 * Kopfzeile. Extrahiert sind Prüfidentifikatoren-Tabellen bislang nur für
 * `202610` und `202710`; bei `202604` läuft der Viewer leer. Dann steht hier
 * ein Satz, der das sagt, und die Stände, die es gibt — keine leere Fläche.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Card, Badge, Button, Notice, Icon,
  Breadcrumbs, Breadcrumb,
  Select, SelectItem,
} from "@conuti-das/prince-ui";
import { Flex, Spinner, princeIcon } from "@/ui-prince";
import { useRelease, useSyncReleaseFromRoute } from "@/context";
import artifactsData from "@/data/generated/artifacts.json";
import { ALL } from "@/types/pipeline";
import type { ArtifactIndex, PruefidSet, PruefidTableDoc } from "@/types";

const PAGE_SIZE = 300;

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

function downloadCsv(filename: string, header: string[], rows: string[][]): void {
  const lines = [header, ...rows].map((r) => r.map(csvCell).join(","));
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function cellText(v: string | number | null | undefined): string {
  return v == null ? "" : String(v);
}

export function PruefidViewerPage() {
  const { release: routeRelease } = useParams<{ release?: string }>();
  // Deep-Link zuerst: die Route gewinnt und zieht die Kopfzeile nach.
  useSyncReleaseFromRoute(routeRelease);
  const { release: headerRelease } = useRelease();
  const [params, setParams] = useSearchParams();

  const data = artifactsData as unknown as ArtifactIndex;
  const releases = Object.keys(data.pruefidSets ?? {}).sort();
  /**
   * Der ANGEFRAGTE Stand — nicht der, der zufällig Daten hat. Genau darin lag
   * die alte Unehrlichkeit: `/pruefid/202604` fiel still auf 202610 zurück und
   * behauptete Zahlen für einen Stand, den niemand gewählt hatte.
   */
  const activeRelease =
    routeRelease ?? (headerRelease !== ALL ? headerRelease : undefined) ?? releases[0];
  const sets: PruefidSet[] = (activeRelease && data.pruefidSets?.[activeRelease]) || [];

  const sourceParam = params.get("source");
  const activeSet = sets.find((s) => s.source === sourceParam) ?? sets[0];

  const tableParam = params.get("table");
  const activeTable =
    activeSet?.tables.find((t) => t.file === tableParam) ?? activeSet?.tables[0];

  // ---- Tabelle laden -------------------------------------------------------
  const [doc, setDoc] = useState<PruefidTableDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeRelease || !activeSet || !activeTable) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDoc(null);
    const url = `/pruefids/${encodeURIComponent(activeRelease)}/${encodeURIComponent(activeSet.source)}/${encodeURIComponent(activeTable.file)}`;
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status} für ${activeTable.file}`);
        return r.json();
      })
      .then((d) => { if (!cancelled) setDoc(d as PruefidTableDoc); })
      .catch((e) => { if (!cancelled) setError(String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeRelease, activeSet?.source, activeTable?.file]);

  // ---- Filter + Paging -----------------------------------------------------
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  useEffect(() => { setQuery(""); setLimit(PAGE_SIZE); }, [activeTable?.file]);

  const columns = useMemo(() => {
    if (!doc) return [] as { key: string; label: string }[];
    const colMap = doc.meta?.columns ?? {};
    const keys = Object.keys(colMap).length
      ? Object.keys(colMap)
      : Object.keys(doc.rows[0] ?? {});
    // Mehrzeilige Original-Header auf die erste Zeile kürzen
    return keys.map((k) => ({ key: k, label: (colMap[k] ?? k).split("\n")[0] }));
  }, [doc]);

  const filteredRows = useMemo(() => {
    if (!doc) return [];
    const q = query.trim().toLowerCase();
    if (!q) return doc.rows;
    return doc.rows.filter((row) =>
      columns.some((c) => cellText(row[c.key]).toLowerCase().includes(q)),
    );
  }, [doc, query, columns]);

  const visibleRows = filteredRows.slice(0, limit);

  function exportCsv() {
    if (!doc || !activeTable) return;
    downloadCsv(
      `pruefid-${activeRelease}-${activeTable.file.replace(/\.json$/, "")}.csv`,
      columns.map((c) => c.label),
      filteredRows.map((row) => columns.map((c) => cellText(row[c.key]))),
    );
  }

  if (!activeRelease || sets.length === 0) {
    return (
      <div className="page">
        <div className="page-header">
          <h2 className="page-title">
            Prüfidentifikatoren{activeRelease ? ` · Stand ${activeRelease}` : ""}
          </h2>
        </div>
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="info">
            {activeRelease
              ? `Für Stand ${activeRelease} liegen keine Prüfidentifikatoren vor.`
              : "Keine Prüfidentifikatoren-Tabellen in der knowledge-collection gefunden."}
          </Notice>
        </div>
        {releases.length > 0 && (
          <Flex style={{ gap: "var(--s-2)", alignItems: "center", marginTop: "var(--s-3)", flexWrap: "wrap" }}>
            <span>Extrahiert für:</span>
            {releases.map((r) => (
              <Link key={r} to={`/pruefid/${r}`} style={{ textDecoration: "none" }}>
                <Badge tone="teal">{r}</Badge>
              </Link>
            ))}
          </Flex>
        )}
      </div>
    );
  }

  return (
    <div className="page">
      <Breadcrumbs items={[
        { id: "artifacts", label: "Artefakte", href: "/artifacts" },
        { id: "pruefid", label: "Prüfidentifikatoren", href: "/artifacts/pruefidentifikatoren" },
        { id: "release", label: activeRelease ?? "" },
      ]}>
        {(item) => (
          <Breadcrumb id={item.id}>
            {item.href ? <a href={item.href}>{item.label}</a> : item.label}
          </Breadcrumb>
        )}
      </Breadcrumbs>

      <div className="page-header" style={{ marginTop: "var(--s-3)" }}>
        <h2 className="page-title">Prüfidentifikatoren · Release {activeRelease}</h2>
        <div className="page-subtitle">
          {activeSet?.source} — {activeSet?.tables.length} Tabellen
        </div>
      </div>

      <Flex style={{ gap: "var(--s-2)", alignItems: "center", marginBottom: "var(--s-3)", flexWrap: "wrap" }}>
        {releases.length > 1 && releases.map((r) => (
          <Link key={r} to={`/pruefid/${r}`} style={{ textDecoration: "none" }}>
            <Badge tone={r === activeRelease ? "teal" : "neutral"}>{r}</Badge>
          </Link>
        ))}
        {sets.length > 1 && (
          <Flex style={{ gap: "var(--s-1)", alignItems: "center" }}>
            <label htmlFor="pruefid-source" className="prn-field-label">Quelle</label>
            <Select
              aria-label="Quelle auswählen"
              selectedKey={activeSet?.source}
              onSelectionChange={(key) => {
                const next = new URLSearchParams(params);
                next.set("source", String(key));
                next.delete("table");
                setParams(next, { replace: true });
              }}
              style={{ minWidth: "320px" }}
            >
              {sets.map((s) => (
                <SelectItem key={s.source} id={s.source}>{s.source}</SelectItem>
              ))}
            </Select>
          </Flex>
        )}
      </Flex>

      <Flex style={{ gap: "var(--s-1)", marginBottom: "var(--s-3)", flexWrap: "wrap" }}>
        {activeSet?.tables.map((t) => {
          const selected = t.file === activeTable?.file;
          return (
            <Button
              key={t.file}
              variant={selected ? "filled" : "tinted"}
              onPress={() => {
                const next = new URLSearchParams(params);
                next.set("table", t.file);
                if (activeSet) next.set("source", activeSet.source);
                setParams(next, { replace: true });
              }}
            >
              {t.sheet} ({t.rows})
            </Button>
          );
        })}
      </Flex>

      {error && (
        <div style={{ marginBottom: "var(--s-3)" }}>
          <Notice tone="negative">
            Tabelle konnte nicht geladen werden: {error}
          </Notice>
        </div>
      )}

      {loading && (
        <div style={{ display: "flex", justifyContent: "center", margin: "var(--s-5) auto" }}>
          <Spinner size="l" />
        </div>
      )}

      {doc && !loading && (
        <Card>
          <Flex style={{ padding: "var(--s-3)", gap: "var(--s-2)", alignItems: "center", flexWrap: "wrap" }}>
            <input
              className="prn-input"
              placeholder="In allen Spalten suchen…"
              value={query}
              style={{ minWidth: "280px" }}
              onChange={(e) => {
                setQuery(e.target.value);
                setLimit(PAGE_SIZE);
              }}
            />
            <span style={{ color: "var(--sapContent_LabelColor)" }}>
              {filteredRows.length} / {doc.rows.length} Zeilen
            </span>
            <Button
              variant="tinted"
              isDisabled={filteredRows.length === 0}
              onPress={exportCsv}
            >
              <Icon name={princeIcon("download")} size={16} />
              CSV
            </Button>
          </Flex>
          <div style={{ overflowX: "auto", padding: "0 var(--s-3) var(--s-3)" }}>
            <table className="fiori-data-table">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} title={doc.meta?.columns?.[c.key] ?? c.key}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c.key}>{cellText(row[c.key])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredRows.length > limit && (
            <Flex style={{ padding: "0 var(--s-3) var(--s-3)", justifyContent: "center" }}>
              <Button variant="plain" onPress={() => setLimit((l) => l + PAGE_SIZE)}>
                {filteredRows.length - limit} weitere Zeilen anzeigen
              </Button>
            </Flex>
          )}
        </Card>
      )}
    </div>
  );
}
